import type { GoodbyeOutcomeState } from '@/components/servicing/goodbyeLetterErrors';

/** A goodbye-letter outcome worth telling the user about (never a delivered letter). */
export interface LetterNotice {
  state: GoodbyeOutcomeState;
  text: string;
  loanNumber?: string | null;
}

export interface FinalizationResult {
  milestonesUpdated: boolean;
  folderMovedSuccessfully: boolean;
  folderMoveQueued: boolean;
  folderMoveError: string | null;
  milestoneError?: string | null;
  letterNotice?: LetterNotice | null;
}

export interface ToastConfig {
  title: string;
  description: string;
  variant?: 'default' | 'destructive';
}

export function letterNoticeHeadline(state: GoodbyeOutcomeState): string {
  return state === 'unconfirmed' ? 'Delivery not confirmed' : 'Not sent';
}

export function formatLetterNotice(notice: LetterNotice): string {
  return `Goodbye letter for ${notice.loanNumber || 'this loan'}: ${letterNoticeHeadline(notice.state)}. ${notice.text}`;
}

function buildFinalizationStatusToast(result: FinalizationResult): ToastConfig {
  if (!result.milestonesUpdated) {
    const base = 'Payout records created, but Encompass milestones could not be updated. Please update milestones manually in Encompass.';
    return {
      title: 'Milestones Not Updated',
      description: result.milestoneError ? `${base} Error: ${result.milestoneError}` : base,
      variant: 'destructive'
    };
  }

  if (result.folderMovedSuccessfully) {
    return {
      title: 'Reconciliation Finalized',
      description: 'Payout records created. Milestones updated to Completion. Loan moved to Completed Loans folder.'
    };
  }

  if (result.folderMoveQueued) {
    return {
      title: 'Reconciliation Finalized',
      description: 'Payout records created. Milestones updated to Completion. Loan folder move queued for automatic retry.'
    };
  }

  return {
    title: 'Reconciliation Finalized',
    description: `Payout records created. Milestones updated to Completion. Could not move loan to Completed Loans folder${result.folderMoveError ? `: ${result.folderMoveError}` : ''} — please move manually in Encompass.`,
    variant: 'destructive'
  };
}

// Keep a known letter outcome with finalization so the completion message
// cannot suggest that a failed or unconfirmed letter was delivered.
export function buildFinalizationToast(result: FinalizationResult): ToastConfig {
  const statusToast = buildFinalizationStatusToast(result);
  if (!result.letterNotice) {
    return statusToast;
  }
  return {
    ...statusToast,
    description: `${statusToast.description} ${formatLetterNotice(result.letterNotice)}`,
    variant: 'destructive'
  };
}
